"use client";

import { useEffect, useRef } from "react";

/**
 * <SearchOutcomeBeacon/> — Beacon client-side qui propage `cascadeStage`
 * vers /api/track/search APRES le render server-side de /recherche.
 *
 * Pourquoi.
 * La cascade de fallback (`fallbackApplied`) n'est connue QUE par
 * /recherche/page.tsx server-side : c'est cette page qui orchestre
 * `executeSearch` puis les 5 paliers de relâchement (name-dropped-city,
 * specialty-dropped-q, specialty-dropped-city, specialty-parent,
 * advanced-filters-relaxed). Le moteur `searchPhysiciansDb` ne voit
 * qu'un palier à la fois.
 *
 * Avant 390d6aedd (2026-06-22), un `trackSearch` server-side firait à
 * chaque render de /recherche avec ce cascadeStage. Mais ce tracking
 * polluait x60 le dashboard (pageloads SEO, refresh, back nav,
 * navigation interne cornerstone, bots non détectés). Le revert a
 * coupé toute propagation — `cascadeStage` est mort en DB depuis.
 *
 * Solution. Beacon client-side avec triple guard anti-pollution :
 *  1. SENTINEL `?_t=1` — posé par <SearchBar/> juste avant `router.push`.
 *     Absent sur : refresh, back/forward, SEO, navigation interne sans
 *     submit, bots préchargeurs. Présent uniquement sur vrais submits.
 *  2. ONE-SHOT — `fired.current` empêche tout re-fire sur re-render
 *     (changement de filtres locaux, suspense boundary).
 *  3. KEEPALIVE FETCH — fire-and-forget, jamais bloquant pour la nav.
 *
 * Si `?_t=1` est absent, on ne fire RIEN. Le pageload SEO est déjà
 * tracké via trackPageView (PageType=SEARCH_RESULTS) côté server.
 *
 * Cf. recommendation Phase 1.D 2026-06-22 (PR2 cascadeStage cabling).
 */
export function SearchOutcomeBeacon({
  q,
  ville,
  locale,
  cascadeStage,
  resultsCount,
  source = "recherche-page",
}: {
  q: string | null;
  ville: string | null;
  locale: string;
  cascadeStage: string;
  resultsCount: number;
  source?: string;
}) {
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    if (typeof window === "undefined") return;

    // SENTINEL strict : `_t=1` injecté par SearchBar.submitWith() avant
    // router.push. Si absent → c'est un pageload SEO / refresh / back /
    // navigation interne / bot préchargeur → on skip pour ne pas
    // reproduire la pollution x60 qui a motivé le revert 390d6aedd.
    const sentinel = new URLSearchParams(window.location.search).get("_t");
    if (sentinel !== "1") return;

    // Anti-spam minimal : payload doit avoir au moins un signal.
    const qTrim = (q || "").trim();
    const villeTrim = (ville || "").trim();
    if (qTrim.length < 2 && !villeTrim) return;

    fired.current = true;

    try {
      void fetch("/api/track/search", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          q: qTrim || undefined,
          ville: villeTrim || undefined,
          locale,
          source,
          resultsCount,
          cascadeStage,
        }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => {});
    } catch {
      // analytics ne doit jamais empêcher quoi que ce soit
    }
  }, [q, ville, locale, cascadeStage, resultsCount, source]);

  return null;
}
